import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { ProgramForm } from '../../components/programs/ProgramForm';

interface Program {
  id: string;
  name: string;
  description: string;
  mission: string;
  target_population: string;
  geographic_scope: string;
  start_date: string;
  end_date: string;
  budget: string;
  status: string;
}

export function ProgramEditPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [program, setProgram] = useState<Program | null>(null);
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchProgram();
  }, [id]);

  const fetchProgram = async () => {
    if (!id) return;
    
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('programs')
        .select('*')
        .eq('id', id)
        .single();

      if (error) throw error;
      
      // Transform data for form
      setProgram({
        ...data,
        budget: data.budget?.toString() || '',
        start_date: data.start_date || '',
        end_date: data.end_date || '',
        description: data.description || '',
        mission: data.mission || '',
        target_population: data.target_population || '',
        geographic_scope: data.geographic_scope || '',
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load program');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (data: any) => {
    if (!id) return;
    
    setIsSubmitting(true);
    try {
      const { error } = await supabase
        .from('programs')
        .update(data)
        .eq('id', id);

      if (error) throw error;
      navigate(`/programs/${id}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error || !program) {
    return (
      <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
        {error || 'Program not found'}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center space-x-3">
        <Link to={`/programs/${id}`} className="text-gray-400 hover:text-gray-600">
          <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </Link>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Edit Program</h1>
          <p className="mt-1 text-sm text-gray-500">{program.name}</p>
        </div>
      </div>

      {/* Form */}
      <ProgramForm 
        program={program} 
        onSubmit={handleSubmit} 
        isLoading={isSubmitting} 
      />
    </div>
  );
}
